// Assign-staff-to-shift modal for the Attendance page.
// Wires the (already-built) useScheduleStaff mutation → POST /attendance.
// MANAGER+ only; the caller gates the trigger button.

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal, Select, Input, Button, useToast } from '@bbpos/ui';
import { useShifts, useScheduleStaff } from '@/features/shifts';
import { userService } from '@/services/user.service';
import { ApiError } from '@/lib/api-client';

interface AssignStaffModalProps {
  open: boolean;
  onClose: () => void;
  /** Optional location scope for the shift/user lists. */
  locationId?: string;
  tenantId?: string;
}

export function AssignStaffModal({ open, onClose, locationId, tenantId }: AssignStaffModalProps) {
  const { toast } = useToast();
  const schedule = useScheduleStaff();

  const [shiftId, setShiftId] = useState('');
  const [userId, setUserId] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (open) {
      setShiftId('');
      setUserId('');
      setDate('');
      setNotes('');
    }
  }, [open]);

  const shiftsQuery = useShifts({ page: 1, location_id: locationId, is_active: 'true' });
  const usersQuery = useQuery({
    queryKey: ['users', tenantId, 'assign', locationId ?? ''],
    queryFn: () => userService.list({ location_id: locationId, tenant_id: tenantId }),
    enabled: open,
    staleTime: 30_000,
  });

  const shiftOptions = [
    { value: '', label: 'Select a shift…' },
    ...(shiftsQuery.data?.data ?? []).map((s) => ({ value: s.id, label: s.name })),
  ];
  const userOptions = [
    { value: '', label: 'Select a staff member…' },
    ...(usersQuery.data?.data ?? []).map((u) => ({ value: u.id, label: `${u.name} (${u.role})` })),
  ];

  const canSubmit = Boolean(shiftId && userId && date) && !schedule.isPending;

  async function handleSubmit() {
    if (!canSubmit) {
      toast({ title: 'Pick a shift, a staff member, and a date.', variant: 'warning' });
      return;
    }
    try {
      await schedule.mutateAsync({
        shift_id: shiftId,
        user_id: userId,
        shift_date: date,
        notes: notes.trim() || undefined,
      });
      toast({ title: 'Staff assigned to shift', variant: 'success' });
      onClose();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to assign staff',
        variant: 'error',
      });
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Assign Staff to Shift">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Shift *</label>
          <Select value={shiftId} onChange={(v) => setShiftId(typeof v === 'string' ? v : '')} options={shiftOptions} />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Staff member *</label>
          <Select value={userId} onChange={(v) => setUserId(typeof v === 'string' ? v : '')} options={userOptions} />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Date *</label>
          <Input type="date" value={date} onChange={(v) => setDate(typeof v === 'string' ? v : '')} />
        </div>
        <div>
          <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Notes</label>
          <Input value={notes} onChange={(v) => setNotes(typeof v === 'string' ? v : '')} placeholder="Optional" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {schedule.isPending ? 'Assigning…' : 'Assign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
